/**
 * Photos are shrunk on the phone before sending (arqam docs 46–47): longest
 * side 1600 px, JPEG quality 0.7, about 200 KB instead of 3–6 MB. On a weak
 * rural connection that is the difference between seconds and minutes.
 */

export const MAX_SIDE = 1600;
export const QUALITY = 0.7;

/** The size to draw at: longest side at most `max`, never enlarged. */
export function fitWithin(
  width: number,
  height: number,
  max = MAX_SIDE,
): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * The picture upright: `createImageBitmap` with `imageOrientation:
 * 'from-image'` applies the EXIF orientation explicitly (a phone photo taken
 * sideways is stored landscape with a "rotate" tag). Browsers without it
 * (older Safari) get an <img>, which current browsers also draw upright.
 */
async function decodeUpright(file: Blob): Promise<
  CanvasImageSource & {
    width: number;
    height: number;
  }
> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // fall through to <img>
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return Object.assign(img, {
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** The photo as a JPEG of at most 1600 px on its longest side. */
export async function shrinkPhoto(file: Blob): Promise<Blob> {
  const image = await decodeUpright(file);
  const size = fitWithin(image.width, image.height);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas not available');
  // White behind transparent PNGs (JPEG has no transparency).
  context.fillStyle = '#fff';
  context.fillRect(0, 0, size.width, size.height);
  context.drawImage(image, 0, 0, size.width, size.height);
  if ('close' in image && typeof image.close === 'function') image.close();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', QUALITY),
  );
  if (!blob) throw new Error('Could not encode the photo');
  return blob;
}
